import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Collapse, Link, Typography } from '@mui/material';
import {
  CompareArrowsOutlined,
  PointOfSaleOutlined,
  ReceiptLongOutlined,
  StorefrontOutlined,
  TuneOutlined,
  FactCheckOutlined,
} from '@mui/icons-material';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';

interface LightspeedSetupChecklistProps {
  /** Remembers the collapsed state per integration in this browser only. */
  integrationId?: string | null;
}

/**
 * Lightspeed Retail (R-Series) back-office settings. The repo knows no per-account URL pattern
 * (the account id only appears in API paths), so these are generic links; the store signs in to
 * its own account from there.
 */
export const LIGHTSPEED_RETAIL_HOME = 'https://cloud.lightspeedapp.com/';

export const lightspeedChecklistStorageKey = (integrationId?: string | null) =>
  `motzu.lightspeedSetupChecklist.collapsed.${integrationId || 'new'}`;

const readCollapsed = (integrationId?: string | null): boolean => {
  try {
    return window.localStorage.getItem(lightspeedChecklistStorageKey(integrationId)) === '1';
  } catch {
    return false;
  }
};

const writeCollapsed = (integrationId: string | null | undefined, collapsed: boolean) => {
  try {
    window.localStorage.setItem(lightspeedChecklistStorageKey(integrationId), collapsed ? '1' : '0');
  } catch {
    // Private mode or blocked storage: the checklist simply stays open.
  }
};

const Row: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
    <Box sx={{ mt: 0.25, flexShrink: 0, display: 'flex' }}>{icon}</Box>
    <Typography variant="body2">{children}</Typography>
  </Box>
);

/**
 * What Lightspeed still owns after a Motzu sync, and what the store sets up itself (flag
 * `lightspeed_setup_checklist`). Motzu writes items, prices, categories and custom field
 * values; tax classes, shops and stock counts stay in Lightspeed.
 */
const LightspeedSetupChecklist: React.FC<LightspeedSetupChecklistProps> = ({ integrationId }) => {
  const { enabled } = useOrgFeatureFlag('lightspeed_setup_checklist');
  // Rows that point at UI another flag gates are hidden while that capability is off.
  const { enabled: storeConfigOn } = useOrgFeatureFlag('lightspeed_store_config');
  const { enabled: linkOnlyOn } = useOrgFeatureFlag('lightspeed_link_only');
  const { enabled: salesSyncOn } = useOrgFeatureFlag('lightspeed_sales_sync');
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(integrationId));
  useEffect(() => {
    setCollapsed(readCollapsed(integrationId));
  }, [integrationId]);

  if (!enabled) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(integrationId, next);
  };

  const iconProps = { fontSize: 'small' as const };

  return (
    <Alert
      severity="info"
      icon={false}
      data-testid="lightspeed-setup-checklist"
      sx={{ alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
      action={
        <Button size="small" color="inherit" onClick={toggle} sx={{ whiteSpace: 'nowrap' }}>
          {collapsed ? 'Show' : 'Got it'}
        </Button>
      }
    >
      <AlertTitle sx={{ mb: collapsed ? 0 : 0.5 }}>Taxes, shops and stock stay in Lightspeed</AlertTitle>
      <Collapse in={!collapsed}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Motzu syncs items, prices, categories and product details. Lightspeed still owns the settings
          below, so check them once after connecting. We link to Lightspeed Retail&rsquo;s sign-in; open
          your account&rsquo;s Setup from there.
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Row icon={<ReceiptLongOutlined {...iconProps} />}>
            <strong>Tax classes.</strong> Motzu only marks items taxable (<code>tax: true</code>). Which
            rate applies comes from the tax classes in your{' '}
            <Link href={LIGHTSPEED_RETAIL_HOME} target="_blank" rel="noopener noreferrer">
              Lightspeed account settings
            </Link>
            ; Motzu never creates or changes them.
          </Row>
          <Row icon={<StorefrontOutlined {...iconProps} />}>
            <strong>Shops and inventory counts.</strong> Motzu never writes your shops or changes stock
            counts. The one exception is the optional starting quantity, set only when Motzu creates an
            item.
          </Row>
          {salesSyncOn && (
            <Row icon={<PointOfSaleOutlined {...iconProps} />}>
              <strong>Register discounts.</strong> Instant rebates reach the register as Lightspeed
              discounts only when the register-discount switch is on in these settings and your store
              chose <em>Separate discount line</em> under rebate checkout treatment on Store Settings.
              With any other choice Motzu archives the discounts it created.
            </Row>
          )}
          {storeConfigOn && (
            <Row icon={<TuneOutlined {...iconProps} />}>
              <strong>Custom fields.</strong> Create them from the <em>Product Fields</em> tab, then map them
              on the <em>Mapping</em> tab. Motzu does not create fields you did not pick.
            </Row>
          )}
          {linkOnlyOn && (
            <Row icon={<CompareArrowsOutlined {...iconProps} />}>
              <strong>Link-only and Catalog match.</strong> Already have items in Lightspeed? Run the{' '}
              <em>Catalog match</em> scan to link them by UPC, SKU or model, and choose link-only so a sync
              records links without rewriting your items.
            </Row>
          )}
          <Row icon={<FactCheckOutlined {...iconProps} />}>
            <strong>First sync check.</strong> After the first sync, open a few items in Lightspeed and
            confirm the category, price, tax and custom fields look right before syncing the rest.
          </Row>
        </Box>
      </Collapse>
    </Alert>
  );
};

export default LightspeedSetupChecklist;
